import React, { useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Api } from "@configuratorware/configurator-frontendgui/Framework/Api";
import { makeStyles, Typography, Button } from "@material-ui/core";
import clsx from "clsx";
import { setCustomData, updateCustomColor } from "../../Shared/Actions/Actions";

import { t } from "@configuratorware/configurator-frontendgui/Framework/i18n";

import { debounce } from "lodash";

interface InlineCustomDataEditorProps {
  hasName: boolean;
  hasPhone: boolean;
}

const _getHexValue = (color) =>
  color.attributes.find((attr) => attr.identifier === "hex_value").values[0]
    .value;

const useStyles = makeStyles(
  (theme) => ({
    colorList: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexWrap: "wrap",
      gap: 8,
      paddingBottom: theme.spacing(1),
    },

    fontList: {
      display: "flex",
      flexWrap: "wrap",
      gap: 8,
    },

    blockTitle: {
      margin: "16px 0",
    },

    colorTitle: {
      margin: "16px 0",
      fontWeight: "bold",
    },

    fontButton: {
      minWidth: "auto !important",
      height: "auto !important",
      padding: "4px 16px",
      border: "2px solid #666",
      "&.is-selected": {
        backgroundColor: "#666",
        color: "#fff",
      },
    },

    colorTile: {
      boxSizing: "border-box",
      width: 32,
      height: 32,
      borderRadius: 6,
      backgroundColor: "transparent",
      boxShadow: "1px 1px 0 #fff",
      transition: "box-shadow 250ms",
      overflow: "hidden",
      cursor: "pointer",

      "&:hover": {
        boxShadow: "0px 0px 0 2px #fff",
      },

      position: "relative",

      "&::after": {
        content: `""`,
        position: "absolute",
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        background: "linear-gradient(135deg, transparent, #0002)",
      },

      "&.is-selected": {
        boxShadow: "0 0 0 4px #666",
      },
    },
    inputField: {
      outline: "none",
      padding: 8,
      width: "100%",
      marginBottom: 16,
      boxSizing: "border-box",
    },
  }),
  { name: "InlineCustomDataEditor" }
);

const customFonts = [
  { identifier: "Holli", title: "Holli" },
  { identifier: "Gerade", title: "Gerade" },
];

const InlineCustomDataEditor = ({
  hasName,
  hasPhone,
}: InlineCustomDataEditorProps) => {
  const classes = useStyles({});
  const dispatch = useDispatch();

  const [colors, setColors] = useState([]);

  const hasBoth = hasName && hasPhone;

  const {
    configuration,
    variantIdentifier,
    customColor,
    customFont,
    displayName,
    displayPhone,
  } = useSelector((state: any) => ({
    configuration: state.configurator.configuration,
    variantIdentifier: state.configurator.selectedVariantIdentifier,

    customColor: state.configurator.configuration.customdata.textColor,
    customFont: state.configurator.configuration.customdata.textFontFamily,
    displayName: state.configurator.configuration.customdata.displayName,
    displayPhone: state.configurator.configuration.customdata.displayPhone,
  }));

  useEffect(() => {
    /**
     * On mount, get all the color from API
     */
    Api.request(
      {
        url: `options/list/${variantIdentifier}/schriftfarbe`,
        method: "POST",
        data: { configuration },
      },
      ({ data }) => {
        // Add the colors to the state
        setColors(data);
      },
      (error) => {}
    );
  }, []);

  useEffect(() => {
    if (!customColor && colors.length > 0) {
      dispatch(updateCustomColor(colors[0]));
    }
  }, [colors]);

  const handleColorClick = (color) => {
    dispatch(updateCustomColor(color));
  };

  const handleFontClick = (font) => {
    dispatch(setCustomData({ textFontFamily: font.identifier }));
  };

  const onDisplayNameChange = useCallback(
    debounce(
      (value) => {
        dispatch(setCustomData({ displayName: value }))
      },
      1000
    ),
    []
  );

  const onDisplayPhoneChange = useCallback(
    debounce(
      (value) => {
        dispatch(setCustomData({ displayPhone: value }))
      },
      1000
    ),
    []
  );

  const renderNameInput = () => {
    return (
      <input
        onChange={(e) => onDisplayNameChange(e.target.value)}
        maxLength={10}
        onInput = {(e) =>{
          //@ts-ignore
          e.target.value = e.target.value.slice(0,10)
        }}
        defaultValue={displayName}
        className={classes.inputField}
        placeholder="Name"
      />
    );
  };

  const renderPhoneInput = () => {
    return (
      <input
        onChange={(e) => onDisplayPhoneChange(e.target.value)}
        maxLength={19}
        onInput = {(e) =>{
          //@ts-ignore
          e.target.value = e.target.value.slice(0,19)
        }}
        defaultValue={displayPhone}
        className={classes.inputField}
        placeholder="Telefonnummer"
      />
    );
  };

  return (
    <div>
      {hasName && renderNameInput()}

      {hasPhone && renderPhoneInput()}

      {hasName && (
        <>
          <Typography className={classes.blockTitle}>
            {hasBoth
              ? t("custom.common.fontFamilyForName")
              : t("custom.common.fontFamily")}
            :
          </Typography>

          <div className={classes.fontList}>
            {customFonts.map((font) => (
              <Button
                key={font.identifier}
                className={clsx(classes.fontButton, {
                  "is-selected": customFont === font.identifier,
                })}
                size="small"
                onClick={() => handleFontClick(font)}
              >
                {font.title}
              </Button>
            ))}
            {}
          </div>
        </>
      )}

      <Typography className={classes.blockTitle}>
        {t("custom.common.fontColor")}:
      </Typography>
      <Typography className={classes.colorTitle}>
        {customColor?.title}
      </Typography>
      <div className={classes.colorList}>
        {colors.map((color) => (
          <div
            data-color={_getHexValue(color)}
            onClick={() => handleColorClick(color)}
            key={color.identifier}
            className={clsx(classes.colorTile, {
              "is-selected": customColor?.identifier === color.identifier,
            })}
            style={{
              backgroundColor: _getHexValue(color),
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default InlineCustomDataEditor;
